import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  CLASS_SESSION_DAYS_OF_WEEK,
  formatDayOfWeekLabels,
} from "@/shared/lib/class-session-day-of-week";
import { config } from "@/shared/lib/config";
import { formatDate } from "@/shared/lib/date-time";
import { findCondition } from "@/shared/contracts/query-helpers";
import { hydrateLookupItem } from "@/shared/lib/utils";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

const STATUS_LABELS: Record<string, string> = {
  scheduled: "ຈອງແລ້ວ",
  completed: "ເຂົ້າຮຽນແລ້ວ",
  cancelled: "ຍົກເລີກ",
};

function getDateRangeLabel(filters: FilterConditionDTO[]): string | null {
  const dateAnd = filters.find((f) => f.field === "date" && f.op === "and");
  const nested = Array.isArray(dateAnd?.value)
    ? (dateAnd.value as FilterConditionDTO[])
    : [];
  const from =
    nested.find((f) => f.field === "date" && f.op === "gte")?.value ??
    filters.find((f) => f.field === "date" && f.op === "gte")?.value;
  const to =
    nested.find((f) => f.field === "date" && f.op === "lte")?.value ??
    filters.find((f) => f.field === "date" && f.op === "lte")?.value;
  const fromStr = from != null ? String(from).split(" ")[0] : "";
  const toStr = to != null ? String(to).split(" ")[0] : "";
  if (fromStr && toStr) {
    return `${formatDate(fromStr)} – ${formatDate(toStr)}`;
  }
  if (fromStr) return `ແຕ່ ${formatDate(fromStr)}`;
  if (toStr) return `ຈົນຮອດ ${formatDate(toStr)}`;
  return null;
}

export function useClassSessionFilterDisplay(filters: FilterConditionDTO[]) {
  const templateId = findCondition(filters, "templateId")?.value as
    | string
    | undefined;
  const roomId = findCondition(filters, "roomId")?.value as string | undefined;
  const instructorId = findCondition(filters, "instructorId")?.value as
    | string
    | undefined;

  const lookups = useQueries({
    queries: [
      {
        queryKey: ["class-session-filter-label", "template", templateId],
        queryFn: () =>
          templateId
            ? hydrateLookupItem<{ id: string; name: string }>(
                `${config.apiUrl}/classes/templates/lookup`,
                templateId,
              )
            : null,
        enabled: !!templateId,
      },
      {
        queryKey: ["class-session-filter-label", "room", roomId],
        queryFn: () =>
          roomId
            ? hydrateLookupItem<{ id: string; name: string }>(
                `${config.apiUrl}/master/rooms/lookup`,
                roomId,
              )
            : null,
        enabled: !!roomId,
      },
      {
        queryKey: ["class-session-filter-label", "instructor", instructorId],
        queryFn: () =>
          instructorId
            ? hydrateLookupItem<{ id: string; name: string }>(
                `${config.apiUrl}/master/instructors/lookup`,
                instructorId,
              )
            : null,
        enabled: !!instructorId,
      },
    ],
  });

  const items = useMemo(() => {
    const rows: { label: string; value: string }[] = [];

    const dayFilter = findCondition(filters, "dayOfWeek");
    if (
      dayFilter?.op === "in" &&
      Array.isArray(dayFilter.value) &&
      dayFilter.value.length > 0
    ) {
      const days = dayFilter.value.filter(
        (v): v is number => typeof v === "number",
      );
      rows.push({
        label: "ວັນ",
        value: formatDayOfWeekLabels(days),
      });
    }

    const dateLabel = getDateRangeLabel(filters);
    if (dateLabel) {
      rows.push({ label: "ຊ່ວງວັນທີ", value: dateLabel });
    }

    const statusFilter = findCondition(filters, "status");
    if (statusFilter?.op === "eq" && typeof statusFilter.value === "string") {
      rows.push({
        label: "ສະຖານະ",
        value: STATUS_LABELS[statusFilter.value] ?? statusFilter.value,
      });
    }

    if (templateId) {
      rows.push({
        label: "ແມ່ແບບ",
        value: lookups[0].data?.name ?? "…",
      });
    }

    if (roomId) {
      rows.push({
        label: "ຫ້ອງ",
        value: lookups[1].data?.name ?? "…",
      });
    }

    if (instructorId) {
      rows.push({
        label: "ຄູຜູ້ສອນ",
        value: lookups[2].data?.name ?? "…",
      });
    }

    return rows;
  }, [filters, templateId, roomId, instructorId, lookups]);

  const isLoading = lookups.some((q) => q.isLoading);

  return { items, isLoading };
}

export { CLASS_SESSION_DAYS_OF_WEEK };
